import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { getAllProductsAsync } from '../features/products/productsSlice';
import { addToCartAsync } from '../features/cart/cartSlice';
import productCatalog, { getFeaturedProducts, siteConfig } from '../data/productCatalog';
import ProductCard from '../components/ProductCard';

const Home = () => {
    document.title = `${siteConfig.name} - Premium Plants & Seeds`;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const realProducts = useSelector((state) => state.products.products);
    const user = useSelector((state) => state.auth.user);

    useEffect(() => {
        if (!realProducts || realProducts.length === 0) {
            dispatch(getAllProductsAsync());
        }
    }, [dispatch, realProducts]);

    const handleAddToCart = (item) => {
        if (!user) {
            message.warning('Please login to add items to cart');
            navigate('/login');
            return;
        }
        // For catalog items, show info message
        message.info('Browse our catalog for available products!');
        navigate('/products');
    };

    const featuredProducts = getFeaturedProducts();

    return (
        <div>
            {/* Hero Section */}
            <section className="hero-modern">
                <div className="hero-inner">
                    <span className="hero-badge-text">
                        <i className="fas fa-leaf"></i> Premium Quality Plants & Seeds
                    </span>
                    <h1 className="hero-title">
                        A Green Beginning 🌿
                    </h1>
                    <p className="hero-subtitle">
                        {siteConfig.description}
                    </p>
                    <div className="hero-cta-group">
                        <Link to="/products" className="btn-hero-primary">
                            <i className="fas fa-shopping-bag"></i> Shop Now
                        </Link>
                        <Link to="/contact-us" className="btn-hero-outline">
                            <i className="fas fa-phone-alt"></i> Contact Us
                        </Link>
                    </div>
                    <div className="hero-stats">
                        <div className="hero-stat">
                            <span className="hero-stat-number">500+</span>
                            <span className="hero-stat-label">Products</span>
                        </div>
                        <div className="hero-stat">
                            <span className="hero-stat-number">10K+</span>
                            <span className="hero-stat-label">Customers</span>
                        </div>
                        <div className="hero-stat">
                            <span className="hero-stat-number">4.8★</span>
                            <span className="hero-stat-label">Rating</span>
                        </div>
                        <div className="hero-stat">
                            <span className="hero-stat-number">₹{siteConfig.freeShippingThreshold}</span>
                            <span className="hero-stat-label">Free Shipping</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Trust Bar */}
            <section className="trust-bar">
                <div className="trust-bar-inner">
                    <div className="trust-item">
                        <div className="trust-icon"><i className="fas fa-truck"></i></div>
                        <div className="trust-text">
                            <strong>Free Shipping</strong>
                            <span>On orders above ₹{siteConfig.freeShippingThreshold}</span>
                        </div>
                    </div>
                    <div className="trust-item">
                        <div className="trust-icon"><i className="fas fa-leaf"></i></div>
                        <div className="trust-text">
                            <strong>100% Organic</strong>
                            <span>Natural & Fresh</span>
                        </div>
                    </div>
                    <div className="trust-item">
                        <div className="trust-icon"><i className="fas fa-shield-alt"></i></div>
                        <div className="trust-text">
                            <strong>Secure Payment</strong>
                            <span>100% Protected</span>
                        </div>
                    </div>
                    <div className="trust-item">
                        <div className="trust-icon"><i className="fas fa-headset"></i></div>
                        <div className="trust-text">
                            <strong>24/7 Support</strong>
                            <span>Dedicated Help</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Products */}
            {featuredProducts.length > 0 && (
                <section className="section-modern">
                    <div className="section-header-modern">
                        <h2>⭐ Best Sellers</h2>
                        <p>Our most popular plants loved by customers</p>
                    </div>
                    <div className="featured-grid">
                        {featuredProducts.slice(0, 8).map((product) => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                onAddToCart={handleAddToCart}
                                linkTo={`/products/?category=${product.category}`}
                            />
                        ))}
                    </div>
                </section>
            )}

            {/* Shop by Category */}
            <section className="section-modern alt-bg">
                <div className="section-header-modern">
                    <h2>Shop by Category</h2>
                    <p>Explore our wide range of plants, seeds, and essentials</p>
                </div>
                {Object.entries(productCatalog).map(([key, category]) => (
                    <div key={key} className="category-section-modern">
                        <div className="category-section-header">
                            <div>
                                <div className="category-section-title">
                                    <span className="cat-icon">{category.icon}</span>
                                    <h3>{category.title}</h3>
                                </div>
                                <p className="category-section-subtitle">{category.subtitle}</p>
                            </div>
                            <Link to={`/products/?category=${key}`} className="category-see-all">
                                See All <i className="fas fa-arrow-right"></i>
                            </Link>
                        </div>
                        <div className="category-products-scroll">
                            {category.items.map((item) => (
                                <ProductCard
                                    key={item.id}
                                    product={{ ...item, category: key, categoryTitle: category.title }}
                                    onAddToCart={handleAddToCart}
                                    linkTo={`/products/?category=${key}`}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </section>

            {/* Promo Banner */}
            <section className="promo-banner">
                <div className="promo-banner-inner">
                    <span className="promo-badge">Special Offer</span>
                    <h3>Get 20% Off on Your First Order</h3>
                    <p>Use code <strong>GREENSTART</strong> at checkout</p>
                    <Link to="/products" className="btn-promo">
                        <i className="fas fa-shopping-bag"></i> Shop Now
                    </Link>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="section-modern">
                <div className="section-header-modern">
                    <h2>Why Choose A Green Beginning?</h2>
                    <p>We care about every plant we sell</p>
                </div>
                <div className="features-grid-modern">
                    <div className="feature-card-modern">
                        <div className="feature-icon-modern"><i className="fas fa-seedling"></i></div>
                        <h4>Premium Quality</h4>
                        <p>Hand-picked seeds and healthy plants from certified nurseries</p>
                    </div>
                    <div className="feature-card-modern">
                        <div className="feature-icon-modern"><i className="fas fa-box-open"></i></div>
                        <h4>Secure Packaging</h4>
                        <p>Plants delivered in eco-friendly, protective packaging</p>
                    </div>
                    <div className="feature-card-modern">
                        <div className="feature-icon-modern"><i className="fas fa-exchange-alt"></i></div>
                        <h4>Easy Returns</h4>
                        <p>7-day replacement guarantee on damaged plants</p>
                    </div>
                    <div className="feature-card-modern">
                        <div className="feature-icon-modern"><i className="fas fa-book-open"></i></div>
                        <h4>Expert Guidance</h4>
                        <p>Free plant care tips and gardening advice</p>
                    </div>
                </div>
            </section>

            {/* Newsletter */}
            <section className="newsletter-modern">
                <h3>🌱 Join Our Green Community</h3>
                <p>Subscribe for exclusive offers, gardening tips, and new arrivals</p>
                <form className="newsletter-form-modern" onSubmit={(e) => e.preventDefault()}>
                    <input type="email" placeholder="Enter your email address" />
                    <button type="submit">Subscribe</button>
                </form>
            </section>
        </div>
    );
};

export default Home;